/* Qik Joe — granule field

   Ambient layer behind the cold open. Granules fall, drift, and settle into a
   drift along the bottom edge, which is what the product actually does. It is
   deliberately not a snow effect: the particles are small and sparse, the fall
   is slow, and the payoff is the accumulation rather than the motion.

   It exists because the hero has to hold the page without photography. When a
   real winter plate lands this can stay as a top layer or be switched off in
   the theme editor.

   Runs only while the hero is on screen. Under prefers-reduced-motion nothing
   animates — a single settled drift is drawn once, so the composition still
   reads as intended. */

(function () {
  'use strict';

  var MAX_DPR = 2;
  var COLUMN_W = 4;        // px per accumulation column
  var MAX_DRIFT = 0.16;    // fraction of canvas height the drift may reach

  function Field(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    if (!this.ctx) return;

    this.color = canvas.getAttribute('data-granule-color') || '#DCE6F0';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.running = false;
    this.raf = 0;

    this.tick = this.tick.bind(this);
    this.resize = this.resize.bind(this);

    this.resize();
    window.addEventListener('resize', this.resize, { passive: true });

    // The hero sizes itself from svh units and a webfont, so at DOMContentLoaded
    // the canvas can still measure zero. Without this the canvas would keep its
    // 300x150 default forever and get stretched by CSS into blurry blobs.
    if ('ResizeObserver' in window) {
      new ResizeObserver(this.resize).observe(this.canvas);
    }

    if (this.reduced) {
      this.settle(260);
      this.draw();
      return;
    }

    this.observe();
  }

  Field.prototype.resize = function () {
    var rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    this.dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    this.w = rect.width;
    this.h = rect.height;
    this.canvas.width = Math.round(this.w * this.dpr);
    this.canvas.height = Math.round(this.h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.cols = Math.max(8, Math.ceil(this.w / COLUMN_W));
    this.maxDrift = this.h * MAX_DRIFT;
    if (!this.height || this.height.length !== this.cols) {
      this.height = new Float32Array(this.cols);
    }

    // Particle budget scales with area, capped so a wide desktop hero does not
    // become the most expensive thing on the page.
    var target = Math.round(this.w * this.h / 9000);
    this.count = Math.max(30, Math.min(160, target));
    this.seed();
  };

  Field.prototype.seed = function () {
    this.parts = [];
    for (var i = 0; i < this.count; i++) this.parts.push(this.spawn(true));
  };

  Field.prototype.spawn = function (anywhere) {
    return {
      x: Math.random() * this.w,
      y: anywhere ? Math.random() * this.h : -8,
      r: 0.7 + Math.random() * 1.7,
      vy: 14 + Math.random() * 26,          // px per second
      sway: 0.4 + Math.random() * 1.1,
      phase: Math.random() * Math.PI * 2,
      a: 0.28 + Math.random() * 0.42
    };
  };

  Field.prototype.colAt = function (x) {
    var c = Math.floor(x / this.w * this.cols);
    return c < 0 ? 0 : (c >= this.cols ? this.cols - 1 : c);
  };

  Field.prototype.step = function (dt, t) {
    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];
      p.y += p.vy * dt;
      p.x += Math.sin(t * 0.0006 + p.phase) * p.sway * dt * 12;

      if (p.x < -4) p.x = this.w + 4;
      else if (p.x > this.w + 4) p.x = -4;

      var c = this.colAt(p.x);
      var floor = this.h - this.height[c];

      if (p.y >= floor) {
        // Land: raise this column and bleed a little into its neighbours so
        // the drift builds a natural slope instead of a comb of spikes.
        if (this.height[c] < this.maxDrift) {
          this.height[c] += p.r * 0.34;
          if (c > 0) this.height[c - 1] += p.r * 0.14;
          if (c < this.cols - 1) this.height[c + 1] += p.r * 0.14;
        }
        this.parts[i] = this.spawn(false);
      }
    }
  };

  Field.prototype.settle = function (iterations) {
    // Used for the reduced-motion render: run the accumulation without drawing
    // so the drift is already there on first paint.
    this.seed();
    for (var n = 0; n < iterations; n++) this.step(1 / 60, n * 16);
  };

  Field.prototype.draw = function () {
    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.fillStyle = this.color;

    if (this.parts) {
      for (var i = 0; i < this.parts.length; i++) {
        var p = this.parts[i];
        ctx.globalAlpha = p.a;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.moveTo(0, this.h);
    for (var c = 0; c < this.cols; c++) {
      ctx.lineTo(c / this.cols * this.w, this.h - this.height[c]);
    }
    ctx.lineTo(this.w, this.h);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
  };

  Field.prototype.tick = function (now) {
    if (!this.running) return;
    var dt = this.last ? Math.min((now - this.last) / 1000, 0.05) : 0.016;
    this.last = now;
    this.step(dt, now);
    this.draw();
    this.raf = window.requestAnimationFrame(this.tick);
  };

  Field.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this.last = 0;
    this.raf = window.requestAnimationFrame(this.tick);
  };

  Field.prototype.stop = function () {
    this.running = false;
    if (this.raf) window.cancelAnimationFrame(this.raf);
  };

  Field.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) { this.start(); return; }
    new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) self.start();
        else self.stop();
      }
    }, { threshold: 0 }).observe(this.canvas);
  };

  function init(root) {
    var nodes = (root || document).querySelectorAll('[data-granules]');
    for (var i = 0; i < nodes.length; i++) new Field(nodes[i]);
  }

  document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
